/* eslint-disable no-plusplus */
import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
// import { CardKPI } from '@mesh.ai/rabbit';
import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { AgGridReact } from 'ag-grid-react';
import Carousel from 'components/Carousel';
import { capitalize } from 'lodash';
import { useDispatch } from 'react-redux';
import { useSelectedView } from 'reducers/viewsSlice';
import { OneOnOneInsightsTree } from 'screens/oneOnOneRabbit/Insights/features/AgGridTable/OneOnOneInsightsTree';
import { FilterBar } from 'screens/oneOnOneRabbit/Insights/features/AgGridTable/components/FilterBar';
import { convertToCSVOneOnOne, parseCSVOneOnOne } from 'screens/oneOnOneRabbit/Insights/features/AgGridTable/helper';
import { OneOnOneCompletion } from 'screens/oneOnOneRabbit/Insights/features/Graphs/OneOnOneCompletion/OneOnOneCompletion';
import ThemeDistribution from 'screens/oneOnOneRabbit/Insights/features/Graphs/ThemeDistribution/ThemeDistribution';
import OneOnOneHeading from 'screens/oneOnOneRabbit/Insights/features/OneOnOneHeading';
import { useOneOnOneInsightsFiltersData } from 'screens/oneOnOneRabbit/Insights/slice/OneOnOneInsightsFiltersSlice';

import { sendDownloadCSVEvent } from 'constants/mixpanelHelper';

import { PageTabbedNavigationV1 } from 'uiToolkitV1/PageTabbedNavigationV1';
import { PaperWithShadow } from 'uiToolkitV1/Paper';

const StyledCarousel = styled(Carousel)`
  // padding: 8px 0px;
  margin-bottom: 12px;
`;

const CompanyOneOnOneInsightsLandingPage = () => {
  const [cardKPIData, setCardKPIData] = useState([]);
  const [cardDataToShow, setDataToShow] = useState([]);
  const [cardDataToShowForOneonOneSetup, setCardDataToShowForOneonOneSetup] = useState([]);
  const { groupByKeys } = useOneOnOneInsightsFiltersData();
  const groupByKeysName = groupByKeys?.displayName === 'Skip-Manager' ? 'Skip Manager' : groupByKeys?.displayName;
  const dispatch = useDispatch();
  const agGridRef = useRef<AgGridReact | null>();
  const { l3Options: tabs } = useRouteToNavLevels();
  const { l1Module } = useRouteToNavLevels();

  const cohort = useSelectedView();

  const oneOnOneInsightsCSVExport = useCallback(
    ({ exportedRows }: { exportedRows: 'all' | 'filteredAndSorted' }) => {
      sendDownloadCSVEvent({
        eventProperties: {
          source: '1:1s',
          type: 'insights',
          cohortName: cohort?.name,
          cohortId: cohort?.id,
          groupBy: groupByKeys?.displayName,
          splitBy: 'none',
        },
      });
      const data = agGridRef?.current?.api.getDataAsCsv({
        fileName: 'one_on_one_insights_table.csv',
        allColumns: true,
        processCellCallback(params): string {
          const {
            value,
            // @ts-ignore
            column: { colId },
          } = params;

          if (colId === '1' || colId === 'ag-Grid-AutoColumn') {
            return value?.displayName;
          }
          // id for Topic Distribution
          if (colId === '9') {
            if (value !== undefined && value !== null) {
              const topicDistributionValues = Object.entries(value)
                .map(([category, value]) => `${capitalize(category)}: ${value}%`)
                .join(', ');

              return topicDistributionValues || 'NA';
            }
            return 'NA'; // Handle the case where value is undefined or null
          }
          return value ?? 'NA';
        },
        exportedRows,
      });

      const parsedCsv = parseCSVOneOnOne(data);

      // Filtering rows without "&" in the Hierarchy value
      const filteredData = parsedCsv.filter((row) => row['"Hierarchy"'].includes('&'));

      // Removing the Hierarchy column
      const dataToConvertIntoCsv = filteredData.map((row) => {
        delete row['"Hierarchy"'];
        delete row['""\r'];
        return row;
      });
      const csvData = convertToCSVOneOnOne(dataToConvertIntoCsv);

      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `one_on_one_insights_table ${Date.now()}.csv`;
      a.click();
    },
    [groupByKeys]
  );

  const onModelUpdated = useCallback(() => {
    const newRowDataForManger = [];
    const newRowDataForOneonOneSetup = [];
    agGridRef?.current?.api?.forEachNodeAfterFilterAndSort((row) => {
      if (row.level === 0) {
        // Check if it's a parent row
        newRowDataForManger.push(row.data);
      }
      if (row.level === 1) {
        newRowDataForOneonOneSetup.push(row.data);
      }
    });

    setCardKPIData(newRowDataForManger);
    setCardDataToShowForOneonOneSetup(newRowDataForOneonOneSetup);
  }, []);

  // Function to count unique managers, managers with completion percentage < 50%, and manager 1:1's not set up
  function countManagers(data1, data2) {
    let managersWithLowCompletionCount = 0;
    let managerOneOnOnesNotSetUpCount = 0;
    let managerWithLowSatisfactionScore = 0;
    let noOneOnOne = 0;

    data1?.forEach((item) => {
      const completionPercentage = item['5'] && item['5'].value ? item['5'].value : 0;
      const oneOnOneStatus = item['2'] ? item['2'].value : null;
      const satisfactionScore = item['6'] ? item['6'].value : null;

      if (completionPercentage !== null && completionPercentage < 50) {
        managersWithLowCompletionCount++;
      }

      if (oneOnOneStatus !== null && oneOnOneStatus < 75) {
        noOneOnOne++;
      }

      if (satisfactionScore < 75) {
        managerWithLowSatisfactionScore++;
      }
    });

    data2.forEach((item) => {
      const oneOnOneStatus = item['2'] ? item['2'].value : null;

      if (oneOnOneStatus !== null && oneOnOneStatus === 'Not setup') {
        managerOneOnOnesNotSetUpCount++;
      }
    });

    setDataToShow([
      {
        title: `1:1's that are yet to be setup`,
        value: managerOneOnOnesNotSetUpCount ?? 0,
      },
      {
        title: `${groupByKeysName}s with <75% participation`,
        value: noOneOnOne ?? 0,
      },

      {
        title: `${groupByKeysName}s with < 75% satisfaction`,
        value: managerWithLowSatisfactionScore ?? 0,
      },
      {
        title: `${groupByKeysName}s with < 50% completion`,
        value: managersWithLowCompletionCount ?? 0,
      },
    ]);

    // return {
    //   noOneOnOne,
    //   managersWithLowCompletionCount,
    //   managerOneOnOnesNotSetUpCount,
    //   managerWithLowSatisfactionScore
    // };
  }

  useEffect(() => {
    // running the function whenever groupByKeys changes as onModelUpdate is not called when groupByKeys changes
    onModelUpdated();
  }, [groupByKeys, cohort?.id]);

  useEffect(() => {
    countManagers(cardKPIData, cardDataToShowForOneonOneSetup);
  }, [groupByKeys, cardKPIData, cardDataToShowForOneonOneSetup]);

  // useEffect(() => {
  //   dispatch(setSelectedView({ ...cohort, teamId: cohort.id }));
  //   return () => {
  //     dispatch(setSelectedView({}));
  //   };
  // }, [cohort]);

  return (
    <Box>
      {l1Module === 'company' && <OneOnOneHeading />}
      {l1Module !== 'team' && <PageTabbedNavigationV1 tabs={tabs} />}

      <Flex mb={3} gap={3} h="348px" mt="1">
        <PaperWithShadow w="50%" p={0} h="100%">
          <OneOnOneCompletion isManagePage />
        </PaperWithShadow>
        <PaperWithShadow w="50%" p={0} h="100%">
          <ThemeDistribution />
        </PaperWithShadow>
      </Flex>
      {/* <StyledCarousel slidesToShow={4} slidesToScroll={1} slideMargin="10px" stretchChildren>
        {cardDataToShow?.map((data) => <CardKPI count={data.value} title={data.title} />)}
      </StyledCarousel> */}
      {cardDataToShow?.length > 0 && (
        <MetricCards height="100%" columns="repeat(4, 1fr)" cardTitle="Metrics" cards={cardDataToShow} />
      )}
      <PaperWithShadow p="12px">
        <FilterBar showGroupBy gridRef={agGridRef} downloadCSV={oneOnOneInsightsCSVExport} />
        <OneOnOneInsightsTree onModelUpdated={onModelUpdated} agGridRef={agGridRef} />
      </PaperWithShadow>
    </Box>
  );
};

export default CompanyOneOnOneInsightsLandingPage;
